import { ensureIanaTimeZone, getDateOnlyInTimezone, toUtcFromZoned, } from './dateTime.js';
/**
 * Fields an administrator may change from bulk event settings.
 * Patch keys match the admin Event Details / Event Settings payloads.
 * `description` is stored on the event as `summary`.
 */
export const BULK_EVENT_SETTINGS_FIELDS = [
    'isVisible',
    'eventVisibility',
    'generalOnsale',
    'facilityFees',
    'includeTaxes',
    'includeFees',
    'includeServiceFees',
    'ticketLimit',
    'globalTicketLimit',
    'resaleEnableMinimum',
    'resaleMinimumPercentage',
    'waitingRoomEnabled',
    'waitingRoomMaxConcurrent',
    'waitingRoomAdmissionTtlMinutes',
    'image',
    'description',
    'category',
    'enableResale',
    'promotionalDescription',
    'invoiceEmailNote',
    'sub_categories',
];
export const BULK_EVENT_SETTINGS_FIELD_LABELS = {
    isVisible: 'Fan visibility',
    eventVisibility: 'Scheduled visibility',
    generalOnsale: 'General onsale',
    facilityFees: 'Facility fees',
    includeTaxes: 'Include taxes',
    includeFees: 'Include facility fees',
    includeServiceFees: 'Include service fees',
    ticketLimit: 'Ticket limit',
    globalTicketLimit: 'Ticket limit amount',
    resaleEnableMinimum: 'Resale minimum',
    resaleMinimumPercentage: 'Resale minimum percentage',
    waitingRoomEnabled: 'Waiting room',
    waitingRoomMaxConcurrent: 'Waiting room capacity',
    waitingRoomAdmissionTtlMinutes: 'Waiting room admission length',
    image: 'Image',
    description: 'Description',
    category: 'Category',
    enableResale: 'Allow resale',
    promotionalDescription: 'Promotional description',
    invoiceEmailNote: 'Invoice email note',
    sub_categories: 'Subcategories',
};
/** Stored column for each patch key. Description is the only rename. */
export const BULK_EVENT_SETTINGS_COLUMNS = {
    isVisible: 'isVisible',
    eventVisibility: 'eventVisibility',
    generalOnsale: 'generalOnsale',
    facilityFees: 'facilityFees',
    includeTaxes: 'includeTaxes',
    includeFees: 'includeFees',
    includeServiceFees: 'includeServiceFees',
    ticketLimit: 'ticketLimit',
    globalTicketLimit: 'globalTicketLimit',
    resaleEnableMinimum: 'resaleEnableMinimum',
    resaleMinimumPercentage: 'resaleMinimumPercentage',
    waitingRoomEnabled: 'waitingRoomEnabled',
    waitingRoomMaxConcurrent: 'waitingRoomMaxConcurrent',
    waitingRoomAdmissionTtlMinutes: 'waitingRoomAdmissionTtlMinutes',
    image: 'image',
    description: 'summary',
    category: 'category',
    enableResale: 'enableResale',
    promotionalDescription: 'promotionalDescription',
    invoiceEmailNote: 'invoiceEmailNote',
    sub_categories: 'sub_categories',
};
/**
 * Explicitly rejected. Supplying any of these fails the request before
 * any event is written.
 */
export const EXCLUDED_BULK_EVENT_SETTINGS_FIELDS = {
    venue: 'Venue cannot be changed with bulk settings.',
    room: 'Room cannot be changed with bulk settings.',
    seatmap: 'Seatmap cannot be changed with bulk settings.',
    seatMap: 'Seatmap cannot be changed with bulk settings.',
    publish: 'Publish is not a bulk settings change.',
    unpublish: 'Unpublish is not a bulk settings change.',
    delete: 'Delete is not a bulk settings change.',
    offers: 'Offers cannot be changed with bulk settings.',
    priceLevels: 'Price levels cannot be changed with bulk settings.',
    holds: 'Holds cannot be changed with bulk settings.',
    kills: 'Kills cannot be changed with bulk settings.',
    dynamicPricing: 'Dynamic pricing cannot be changed with bulk settings.',
    name: 'Event name cannot be changed with bulk settings.',
    eventName: 'Event name cannot be changed with bulk settings.',
    start: 'Start time cannot be changed with bulk settings.',
    startTime: 'Start time cannot be changed with bulk settings.',
    end: 'End time cannot be changed with bulk settings.',
    endTime: 'End time cannot be changed with bulk settings.',
    doorsOpen: 'Doors time cannot be changed with bulk settings.',
    doorsTime: 'Doors time cannot be changed with bulk settings.',
    status: 'Event status cannot be changed with bulk settings.',
};
export const BULK_SETTINGS_RESULT_STATUS = {
    SUCCESS: 'SUCCESS',
    SKIPPED: 'SKIPPED',
    FAILED: 'FAILED',
};
const ALLOWED = new Set(BULK_EVENT_SETTINGS_FIELDS);
const WAITING_ROOM_FIELDS = [
    'waitingRoomEnabled',
    'waitingRoomMaxConcurrent',
    'waitingRoomAdmissionTtlMinutes',
];
const FEE_FLAG_FIELDS = [
    'includeTaxes',
    'includeFees',
    'includeServiceFees',
];
const PUBLISHED_STATUSES = new Set(['published', 'on_sale', 'publishing']);
const TICKET_LIMIT_MESSAGE = 'Ticket limit must be a number between 1 and 50';
const RESALE_MINIMUM_MESSAGE = 'Minimum resale price cannot be set below 30%';
const PROMOTIONAL_DESCRIPTION_MAX = 320;
const INVOICE_EMAIL_NOTE_MAX = 1000;
const SUBCATEGORY_CATEGORY_MESSAGE = "Subcategories must belong to the event's category.";
export function ticketLimitErrorMessage(value) {
    if (value === '' || value == null)
        return null;
    const parsed = parseInt(String(value), 10);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 50) {
        return TICKET_LIMIT_MESSAGE;
    }
    return null;
}
export function resaleMinimumErrorMessage(value) {
    if (value === '' || value == null)
        return null;
    const parsed = parseFloat(String(value));
    if (!Number.isFinite(parsed) || parsed < 30) {
        return RESALE_MINIMUM_MESSAGE;
    }
    return null;
}
function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
function isBoolean(value) {
    return typeof value === 'boolean';
}
function finiteNumber(value) {
    if (typeof value === 'number' && Number.isFinite(value))
        return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed))
            return parsed;
    }
    return null;
}
function positiveInteger(value) {
    const parsed = finiteNumber(value);
    if (parsed == null || !Number.isInteger(parsed) || parsed <= 0)
        return null;
    return parsed;
}
function parseInstant(value) {
    if (value == null || value === '')
        return null;
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime()))
        return null;
    return date;
}
export function validateUniversalBulkSettingsPatch(patch, options = {}) {
    if (!isPlainObject(patch)) {
        return {
            ok: false,
            issues: [
                {
                    code: 'invalid_patch',
                    message: 'Settings patch must be an object.',
                },
            ],
        };
    }
    const keys = Object.keys(patch);
    if (keys.length === 0) {
        return {
            ok: false,
            issues: [
                {
                    code: 'empty_patch',
                    message: 'Choose at least one setting to update.',
                },
            ],
        };
    }
    const issues = [];
    const waitingRoomLive = options.waitingRoomFeatureEnabled === true;
    keys.forEach((key) => {
        if (EXCLUDED_BULK_EVENT_SETTINGS_FIELDS[key]) {
            issues.push({
                code: 'unsupported_field',
                field: key,
                message: EXCLUDED_BULK_EVENT_SETTINGS_FIELDS[key],
            });
            return;
        }
        if (!ALLOWED.has(key)) {
            issues.push({
                code: 'unsupported_field',
                field: key,
                message: `${key} is not a supported bulk settings field.`,
            });
            return;
        }
        if (!waitingRoomLive &&
            WAITING_ROOM_FIELDS.includes(key)) {
            issues.push({
                code: 'unsupported_field',
                field: key,
                message: 'Waiting room settings are not enabled.',
            });
        }
    });
    if (issues.length > 0) {
        return { ok: false, issues };
    }
    const valueIssues = validateUniversalValues(patch);
    if (valueIssues.length > 0) {
        return { ok: false, issues: valueIssues };
    }
    return { ok: true, fields: keys };
}
function validateUniversalValues(patch) {
    const issues = [];
    const requireBoolean = (field) => {
        if (patch[field] !== undefined && !isBoolean(patch[field])) {
            issues.push({
                code: 'invalid_value',
                field,
                message: `${BULK_EVENT_SETTINGS_FIELD_LABELS[field]} must be true or false.`,
            });
        }
    };
    requireBoolean('isVisible');
    requireBoolean('ticketLimit');
    requireBoolean('resaleEnableMinimum');
    requireBoolean('waitingRoomEnabled');
    requireBoolean('enableResale');
    FEE_FLAG_FIELDS.forEach((field) => requireBoolean(field));
    if (patch.eventVisibility !== undefined && patch.eventVisibility !== null) {
        if (!parseInstant(patch.eventVisibility)) {
            issues.push({
                code: 'invalid_value',
                field: 'eventVisibility',
                message: 'Scheduled visibility must be a valid date.',
            });
        }
    }
    if (patch.generalOnsale !== undefined) {
        if (patch.generalOnsale == null || !parseInstant(patch.generalOnsale)) {
            issues.push({
                code: 'invalid_value',
                field: 'generalOnsale',
                message: 'General onsale is required and must be a valid date.',
            });
        }
    }
    if (patch.facilityFees !== undefined) {
        const amount = finiteNumber(patch.facilityFees);
        if (amount == null || amount < 0) {
            issues.push({
                code: 'invalid_value',
                field: 'facilityFees',
                message: 'Facility fee must be a number zero or greater.',
            });
        }
    }
    if (patch.ticketLimit === true) {
        const message = ticketLimitErrorMessage(patch.globalTicketLimit);
        if (patch.globalTicketLimit == null ||
            patch.globalTicketLimit === '' ||
            message) {
            issues.push({
                code: 'invalid_value',
                field: 'globalTicketLimit',
                message: message || TICKET_LIMIT_MESSAGE,
            });
        }
    }
    else if (patch.globalTicketLimit !== undefined &&
        patch.globalTicketLimit !== null &&
        patch.globalTicketLimit !== '') {
        const message = ticketLimitErrorMessage(patch.globalTicketLimit);
        if (message) {
            issues.push({
                code: 'invalid_value',
                field: 'globalTicketLimit',
                message,
            });
        }
    }
    if (patch.resaleEnableMinimum === true) {
        const message = resaleMinimumErrorMessage(patch.resaleMinimumPercentage);
        if (patch.resaleMinimumPercentage == null ||
            patch.resaleMinimumPercentage === '' ||
            message) {
            issues.push({
                code: 'invalid_value',
                field: 'resaleMinimumPercentage',
                message: message || RESALE_MINIMUM_MESSAGE,
            });
        }
    }
    else if (patch.resaleMinimumPercentage !== undefined &&
        patch.resaleMinimumPercentage !== null &&
        patch.resaleMinimumPercentage !== '') {
        const message = resaleMinimumErrorMessage(patch.resaleMinimumPercentage);
        if (message) {
            issues.push({
                code: 'invalid_value',
                field: 'resaleMinimumPercentage',
                message,
            });
        }
    }
    if (patch.waitingRoomEnabled === true) {
        const concurrent = positiveInteger(patch.waitingRoomMaxConcurrent);
        const ttl = positiveInteger(patch.waitingRoomAdmissionTtlMinutes);
        if (concurrent == null) {
            issues.push({
                code: 'invalid_value',
                field: 'waitingRoomMaxConcurrent',
                message: 'Max concurrent shoppers must be at least 1.',
            });
        }
        if (ttl == null) {
            issues.push({
                code: 'invalid_value',
                field: 'waitingRoomAdmissionTtlMinutes',
                message: 'Admission session length must be at least 1 minute.',
            });
        }
    }
    if (patch.image !== undefined) {
        if (positiveInteger(patch.image) == null) {
            issues.push({
                code: 'invalid_value',
                field: 'image',
                message: 'Image is required.',
            });
        }
    }
    if (patch.description !== undefined &&
        typeof patch.description !== 'string') {
        issues.push({
            code: 'invalid_value',
            field: 'description',
            message: 'Description must be text.',
        });
    }
    else if (typeof patch.description === 'string' &&
        patch.description.length > 20000) {
        issues.push({
            code: 'invalid_value',
            field: 'description',
            message: 'Description is too long.',
        });
    }
    if (patch.category !== undefined && patch.category !== null) {
        if (positiveInteger(patch.category) == null) {
            issues.push({
                code: 'invalid_value',
                field: 'category',
                message: 'Category must be a valid category.',
            });
        }
    }
    if (patch.promotionalDescription !== undefined) {
        if (typeof patch.promotionalDescription !== 'string') {
            issues.push({
                code: 'invalid_value',
                field: 'promotionalDescription',
                message: 'Promotional description must be text.',
            });
        }
        else if (normalizePromotionalDescription(patch.promotionalDescription).length >
            PROMOTIONAL_DESCRIPTION_MAX) {
            issues.push({
                code: 'invalid_value',
                field: 'promotionalDescription',
                message: `Promotional description must be ${PROMOTIONAL_DESCRIPTION_MAX} characters or fewer.`,
            });
        }
    }
    if (patch.invoiceEmailNote !== undefined) {
        if (typeof patch.invoiceEmailNote !== 'string') {
            issues.push({
                code: 'invalid_value',
                field: 'invoiceEmailNote',
                message: 'Invoice email note must be text.',
            });
        }
        else if (normalizeInvoiceEmailNote(patch.invoiceEmailNote).length >
            INVOICE_EMAIL_NOTE_MAX) {
            issues.push({
                code: 'invalid_value',
                field: 'invoiceEmailNote',
                message: `Invoice email note must be ${INVOICE_EMAIL_NOTE_MAX} characters or fewer.`,
            });
        }
    }
    if (patch.sub_categories !== undefined) {
        if (!Array.isArray(patch.sub_categories)) {
            issues.push({
                code: 'invalid_value',
                field: 'sub_categories',
                message: 'Subcategories must be a list.',
            });
        }
        else if (patch.sub_categories.some((id) => positiveInteger(id) == null)) {
            issues.push({
                code: 'invalid_value',
                field: 'sub_categories',
                message: 'Each subcategory must be a valid subcategory.',
            });
        }
    }
    return issues;
}
function normalizePromotionalDescription(value) {
    return String(value ?? '')
        .replace(/\s+/g, ' ')
        .trim();
}
function promotionalDescriptionToText(value) {
    if (typeof value === 'string')
        return normalizePromotionalDescription(value);
    if (!Array.isArray(value))
        return '';
    const parts = [];
    const walk = (node) => {
        if (!node || typeof node !== 'object')
            return;
        const record = node;
        if (typeof record.text === 'string')
            parts.push(record.text);
        if (Array.isArray(record.children))
            record.children.forEach(walk);
    };
    value.forEach(walk);
    return normalizePromotionalDescription(parts.join(' '));
}
function textToPromotionalBlocks(value) {
    const text = normalizePromotionalDescription(value);
    if (!text)
        return null;
    return [
        {
            type: 'paragraph',
            children: [{ type: 'text', text }],
        },
    ];
}
function normalizeInvoiceEmailNote(value) {
    return String(value ?? '')
        .replace(/\r\n/g, '\n')
        .trim();
}
function subcategoryIds(value) {
    if (!Array.isArray(value))
        return [];
    const ids = value
        .map((item) => relationId(item))
        .filter((id) => id != null);
    return [...new Set(ids)].sort((left, right) => left - right);
}
function sameIds(previous, next) {
    const left = Array.isArray(previous) ? previous : [];
    const right = Array.isArray(next) ? next : [];
    if (left.length !== right.length)
        return false;
    return left.every((id, index) => Number(id) === Number(right[index]));
}
function relationId(value) {
    if (value == null || value === '')
        return null;
    if (typeof value === 'object') {
        return positiveInteger(value.id);
    }
    return positiveInteger(value);
}
function isVisibilityLocked(event) {
    return PUBLISHED_STATUSES.has(String(event.status || ''));
}
function isGeneralOnsaleLocked(event, now) {
    if (event.status === 'complete' || event.status === 'disabled')
        return false;
    if (event.status !== 'published' && event.status !== 'on_sale')
        return false;
    if (event.status === 'on_sale')
        return true;
    const onsale = parseInstant(event.generalOnsale);
    if (onsale && onsale.getTime() <= now.getTime())
        return true;
    return (event.activeOfferStarts || []).some((start) => {
        const instant = parseInstant(start);
        return Boolean(instant && instant.getTime() <= now.getTime());
    });
}
function isAfterEventStart(candidate, event) {
    if (event.date_tbd || event.start_tbd)
        return false;
    const start = parseInstant(event.start);
    if (!start)
        return false;
    if (event.time_tbd) {
        const tz = ensureIanaTimeZone(event.timezone, 'UTC');
        const day = getDateOnlyInTimezone(start, tz);
        if (!day)
            return candidate.getTime() > start.getTime();
        const endOfDay = toUtcFromZoned(`${day}T23:59:59`, tz);
        return candidate.getTime() > endOfDay.getTime();
    }
    return candidate.getTime() > start.getTime();
}
function afterStartMessage(event) {
    if (event.time_tbd)
        return 'Time cannot be after the end of the event date.';
    return 'Time cannot be after the event start time.';
}
function currentValue(event, field) {
    switch (field) {
        case 'description':
            return event.summary ?? '';
        case 'promotionalDescription':
            return promotionalDescriptionToText(event.promotionalDescription);
        case 'invoiceEmailNote':
            return normalizeInvoiceEmailNote(event.invoiceEmailNote);
        case 'enableResale':
            return Boolean(event.enableResale);
        case 'sub_categories':
            return subcategoryIds(event.sub_categories);
        case 'image':
            return relationId(event.image);
        case 'category':
            return relationId(event.category);
        case 'facilityFees':
            return event.facilityFees == null ? null : Number(event.facilityFees);
        case 'resaleMinimumPercentage':
            return event.resaleMinimumPercentage == null ||
                event.resaleMinimumPercentage === ''
                ? null
                : Number(event.resaleMinimumPercentage);
        case 'globalTicketLimit':
            return event.globalTicketLimit == null || event.globalTicketLimit === ''
                ? null
                : Number(event.globalTicketLimit);
        default:
            return event[field];
    }
}
function nextStoredValue(field, raw) {
    switch (field) {
        case 'description':
            return typeof raw === 'string' ? raw : '';
        case 'promotionalDescription':
            return textToPromotionalBlocks(raw);
        case 'invoiceEmailNote':
            return normalizeInvoiceEmailNote(raw);
        case 'sub_categories':
            return subcategoryIds(raw);
        case 'image':
        case 'category':
            return raw == null ? null : positiveInteger(raw);
        case 'facilityFees':
        case 'resaleMinimumPercentage':
            return raw == null || raw === '' ? null : finiteNumber(raw);
        case 'globalTicketLimit':
            return raw == null || raw === '' ? null : parseInt(String(raw), 10);
        case 'waitingRoomMaxConcurrent':
        case 'waitingRoomAdmissionTtlMinutes':
            return positiveInteger(raw);
        case 'eventVisibility':
            return raw == null ? null : new Date(String(raw)).toISOString();
        case 'generalOnsale':
            return new Date(String(raw)).toISOString();
        default:
            return raw;
    }
}
function valuesMatch(field, previous, next) {
    if (field === 'eventVisibility' || field === 'generalOnsale') {
        const left = parseInstant(previous);
        const right = parseInstant(next);
        if (!left && !right)
            return true;
        if (!left || !right)
            return false;
        return left.getTime() === right.getTime();
    }
    if (field === 'facilityFees' ||
        field === 'resaleMinimumPercentage' ||
        field === 'globalTicketLimit' ||
        field === 'image' ||
        field === 'category' ||
        field === 'waitingRoomMaxConcurrent' ||
        field === 'waitingRoomAdmissionTtlMinutes') {
        if (previous == null || next == null) {
            return previous == null && next == null;
        }
        return Number(previous) === Number(next);
    }
    if (field === 'description') {
        return String(previous ?? '') === String(next ?? '');
    }
    if (field === 'promotionalDescription') {
        return (promotionalDescriptionToText(previous) ===
            promotionalDescriptionToText(next));
    }
    if (field === 'invoiceEmailNote') {
        return (normalizeInvoiceEmailNote(previous) === normalizeInvoiceEmailNote(next));
    }
    if (field === 'sub_categories') {
        return sameIds(previous, next);
    }
    return previous === next;
}
const NAIVE_LOCAL_DATE = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2}(?:\.\d+)?)?$/;
function hasExplicitZone(value) {
    return /(?:z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
}
/**
 * Absolute timestamps (with Z or an offset) stay that instant.
 * A clock time with no zone is the venue's local time, matching the
 * single-event editor, so the same entry can land at different UTC instants.
 */
function normalizeBulkSettingsInstant(value, timezone, label) {
    if (value == null || value === '')
        return { ok: true, value: null };
    const raw = String(value).trim();
    if (hasExplicitZone(raw)) {
        const instant = parseInstant(raw);
        if (!instant)
            return { ok: false, message: `${label} must be a valid date.` };
        return { ok: true, value: instant.toISOString() };
    }
    const match = raw.match(NAIVE_LOCAL_DATE);
    if (!match)
        return { ok: false, message: `${label} must be a valid date.` };
    const zone = typeof timezone === 'string' && timezone.trim().toUpperCase() === 'UTC'
        ? 'UTC'
        : ensureIanaTimeZone(timezone, '');
    if (!zone) {
        return {
            ok: false,
            message: `${label} needs the event's venue timezone.`,
        };
    }
    try {
        const local = match[2] ? raw : `${match[1]}:00`;
        const utc = toUtcFromZoned(local, zone);
        if (!utc || Number.isNaN(utc.getTime())) {
            return { ok: false, message: `${label} must be a valid date.` };
        }
        return { ok: true, value: utc.toISOString() };
    }
    catch {
        return { ok: false, message: `${label} must be a valid date.` };
    }
}
function normalizeSettingsPatchDates(event, patch) {
    const next = { ...patch };
    for (const field of ['eventVisibility', 'generalOnsale']) {
        if (!Object.prototype.hasOwnProperty.call(next, field))
            continue;
        const normalized = normalizeBulkSettingsInstant(next[field], event.timezone, BULK_EVENT_SETTINGS_FIELD_LABELS[field]);
        if (!normalized.ok)
            return normalized;
        next[field] = normalized.value;
    }
    return { ok: true, patch: next };
}
function emptyPlan(disposition, status, reason, reasonCode) {
    return {
        disposition,
        status,
        reason,
        reasonCode,
        updatedFields: [],
        skippedFields: [],
        write: {},
        oldValues: {},
        newValues: {},
    };
}
/**
 * Per-event plan. Universal patch shape is assumed valid.
 * Locks skip a field. Event-specific date rules fail that event with no write.
 * A lock on one field does not block other eligible fields.
 */
export function planEventSettingsUpdate(event, patch, options = {}) {
    const now = options.now || new Date();
    if (event.status === 'disabled') {
        return emptyPlan('skip', 'SKIPPED', 'Event has been deleted.', 'deleted');
    }
    if (event.status === 'complete') {
        return emptyPlan('skip', 'SKIPPED', 'Event has ended. Settings are locked.', 'business_rule');
    }
    if (event.status === 'publishing') {
        return emptyPlan('skip', 'SKIPPED', 'Event is publishing and cannot be edited.', 'business_rule');
    }
    const normalizedDates = normalizeSettingsPatchDates(event, patch);
    if (!normalizedDates.ok) {
        return emptyPlan('fail', 'FAILED', normalizedDates.message, 'validation');
    }
    patch = normalizedDates.patch;
    const fields = Object.keys(patch).filter((key) => ALLOWED.has(key));
    const skippedFields = [];
    const validationMessages = [];
    const write = {};
    const oldValues = {};
    const newValues = {};
    const updatedFields = [];
    const visibilityLocked = isVisibilityLocked(event);
    const onsaleLocked = isGeneralOnsaleLocked(event, now);
    const nextVisibility = patch.eventVisibility !== undefined
        ? patch.eventVisibility
        : event.eventVisibility;
    const nextOnsale = patch.generalOnsale !== undefined
        ? patch.generalOnsale
        : event.generalOnsale;
    fields.forEach((field) => {
        if (field === 'eventVisibility' && visibilityLocked) {
            skippedFields.push({
                field,
                reason: 'Scheduled visibility is locked after the event is published.',
            });
            return;
        }
        if (field === 'generalOnsale' && onsaleLocked) {
            skippedFields.push({
                field,
                reason: 'General onsale is locked after the event is on sale.',
            });
            return;
        }
        if (field === 'eventVisibility' && patch.eventVisibility) {
            const instant = parseInstant(patch.eventVisibility);
            if (instant && isAfterEventStart(instant, event)) {
                validationMessages.push(`Scheduled visibility: ${afterStartMessage(event)}`);
                return;
            }
            const onsale = parseInstant(nextOnsale);
            if (instant && onsale && instant.getTime() > onsale.getTime()) {
                validationMessages.push('Scheduled visibility cannot be after general onsale.');
                return;
            }
        }
        if (field === 'generalOnsale' && patch.generalOnsale) {
            const instant = parseInstant(patch.generalOnsale);
            if (instant && isAfterEventStart(instant, event)) {
                validationMessages.push(`General onsale: ${afterStartMessage(event)}`);
                return;
            }
            const visibility = parseInstant(nextVisibility);
            if (instant &&
                visibility &&
                instant.getTime() < visibility.getTime() &&
                !visibilityLocked) {
                validationMessages.push('General onsale cannot be before scheduled visibility.');
                return;
            }
            if (instant &&
                visibility &&
                visibilityLocked &&
                instant.getTime() < visibility.getTime()) {
                validationMessages.push('General onsale cannot be before scheduled visibility.');
                return;
            }
        }
        if (field === 'sub_categories') {
            const allowed = new Set((event.allowedSubCategoryIds || [])
                .map((id) => positiveInteger(id))
                .filter((id) => id != null));
            const invalid = subcategoryIds(patch.sub_categories).filter((id) => !allowed.has(id));
            if (invalid.length > 0) {
                validationMessages.push(SUBCATEGORY_CATEGORY_MESSAGE);
                return;
            }
        }
        const previous = currentValue(event, field);
        const next = nextStoredValue(field, patch[field]);
        if (valuesMatch(field, previous, next)) {
            skippedFields.push({
                field,
                reason: `${BULK_EVENT_SETTINGS_FIELD_LABELS[field]} already has this value.`,
            });
            return;
        }
        write[BULK_EVENT_SETTINGS_COLUMNS[field]] = next;
        oldValues[field] = previous;
        newValues[field] =
            field === 'promotionalDescription'
                ? promotionalDescriptionToText(next)
                : next;
        updatedFields.push(field);
    });
    if (validationMessages.length === 0 &&
        !fields.includes('sub_categories') &&
        Object.prototype.hasOwnProperty.call(write, 'category')) {
        const existing = subcategoryIds(event.sub_categories);
        if (existing.length > 0) {
            write.sub_categories = [];
            oldValues.sub_categories = existing;
            newValues.sub_categories = [];
            updatedFields.push('sub_categories');
        }
    }
    if (validationMessages.length > 0) {
        return {
            ...emptyPlan('fail', 'FAILED', validationMessages.join(' '), 'validation'),
            skippedFields,
        };
    }
    if (updatedFields.length === 0) {
        const locked = skippedFields.filter((field) => !field.reason.includes('already has this value'));
        if (locked.length > 0 && locked.length === skippedFields.length) {
            return {
                ...emptyPlan('skip', 'SKIPPED', locked.map((field) => field.reason).join(' '), 'locked'),
                skippedFields,
            };
        }
        return {
            ...emptyPlan('skip', 'SKIPPED', skippedFields.length
                ? skippedFields.map((field) => field.reason).join(' ')
                : 'Settings already match this event.', skippedFields.some((field) => field.reason.includes('already has this value'))
                ? 'unchanged'
                : 'business_rule'),
            skippedFields,
        };
    }
    const lockNote = skippedFields
        .filter((field) => !field.reason.includes('already has this value'))
        .map((field) => field.reason);
    const reason = lockNote.length
        ? `Updated ${updatedFields
            .map((field) => BULK_EVENT_SETTINGS_FIELD_LABELS[field])
            .join(', ')}. Skipped: ${lockNote.join(' ')}`
        : `Updated ${updatedFields
            .map((field) => BULK_EVENT_SETTINGS_FIELD_LABELS[field])
            .join(', ')}.`;
    return {
        disposition: 'update',
        status: 'SUCCESS',
        reason,
        reasonCode: 'applied',
        updatedFields,
        skippedFields,
        write,
        oldValues,
        newValues,
    };
}
